import type { Middleware, ResponseContext, WeftParameters } from '@vy/client';
import { getApi } from '@vy/client';
import { isEmptyString } from '@vy/utils';
import { isEmpty } from 'lodash';
import { getRequiredToken, getToken, logout, refreshToken } from '../user-auth';
import { BAD_AUTH_DATA_CODES } from './api-consts';
import type { PostResponseMiddleware } from './api-types/post-response-middleware';

export function getPrivateApi() {
  return buildApi(true);
}

export function getPublicApi() {
  return buildApi(false);
}

function buildApi(privateEndpoint: boolean) {
  const configurationParameters = buildConfigurationParams(privateEndpoint);

  return getApi(configurationParameters);
}

function getBasePath() {
  return import.meta.env.VITE_WEFT_API;
}

function buildConfigurationParams(privateEndpoint: boolean): WeftParameters {
  const basePath = getBasePath();

  const token = getToken();
  if (privateEndpoint && isEmptyString(token)) {
    location.reload();
  }

  const accessToken = privateEndpoint ? token : undefined;

  return {
    basePath,
    ...(accessToken ? { accessToken } : {}),
    ...(privateEndpoint ? { middleware: buildMiddlewares() } : {}),
  };
}

function buildMiddlewares(): Middleware[] {
  const post = buildPostResponseMiddleware();

  return [
    {
      post,
    },
  ];
}

function buildPostResponseMiddleware(): PostResponseMiddleware {
  return (context) => {
    if (BAD_AUTH_DATA_CODES.includes(context.response.status)) {
      return new Promise((resolve, reject) => {
        handleUnauthorizedResponse(context).then(resolve, reject);
      });
    }

    return Promise.resolve(context.response);
  };
}

async function handleUnauthorizedResponse(context: ResponseContext): Promise<Response> {
  try {
    await refreshToken();

    return await retryRequestWithResponse(context);
  } catch (error) {
    await logout();
    throw error;
  }
}

async function retryRequestWithResponse(context: ResponseContext) {
  const { fetch, url, init } = context;
  const newInit = buildRetryInit(init);

  return fetch(url, newInit);
}

function buildRetryInit(init: RequestInit) {
  if (isEmpty(init.headers)) {
    return init;
  }

  const headers = buildRetryHeaders(init.headers);

  return {
    ...init,
    headers,
  };
}

function buildRetryHeaders(headers: HeadersInit) {
  const token = getRequiredToken();

  return {
    ...headers,
    Authorization: `Bearer ${token}`,
  };
}
